/**
 * AgentsTab.tsx
 *
 * Operator-facing tab inside ProfileOverlay (Phase 2 / slice 1).
 * Lists every AI agent the viewer's wallet has registered, with a
 * detail drawer showing capabilities, status, and the on-chain
 * spending policy snapshot.
 *
 * Registration: operators can **Register on-chain** below when a wallet
 * signer is connected (Freighter / WalletConnect for `G…`, passkey for
 * `C…`). Separately, MCP `genAgent` + same tx works for automation.
 *
 * Empty / unconfigured states are explicitly handled so the tab is
 * useful even before the agent contracts are deployed:
 *   - Contracts not deployed         → friendly explainer + capability
 *                                       schema preview
 *   - Deployed, no agents yet        → browser registration + MCP option
 *   - Deployed, ≥1 agent             → list + detail drawer (revoke, add another)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listAgents,
  getAgent,
  syncAgent,
  getAgentPolicy,
  getCapabilitySchema,
  buildRegisterTx,
  buildRevokeTx,
  buildSetCapabilitiesTx,
  submitSignedAgentTx,
  type CachedAgentRecord,
  type AgentRecord,
  type AgentPolicySnapshot,
  type CapabilityEntry,
} from '../services/agentService';
import { Networks } from '@stellar/stellar-sdk';
import {
  getPublicProfile,
  type SigningWallet,
  type PasskeySigner,
  type ProfileRecord,
} from '../services/profileService';
import { humanizeUnknownError } from '../utils/sorobanContractErrors';
import {
  getResidencySnapshot,
  type ResidencySnapshot,
} from '../services/citizenshipService';
import {
  runResidencyChangeChain,
  type ResidencyChainStep,
} from '../services/citizenshipPasskeyChain';
import AgentBroadcastPreviewModal from './AgentBroadcastPreviewModal';
import AgentLiveBroadcastModal   from './AgentLiveBroadcastModal';
import AgentDisposeModal, { type DisposeParams } from './AgentDisposeModal';
import AgentMovementPanel from './AgentMovementPanel';
import AgentsMiniGlobe from './AgentsMiniGlobe';
import AgentTeleportModal from './AgentTeleportModal';
import type { ExecuteLivePayment } from './ProfileOverlay';
import type { ApproxLocation } from '../services/liveSessionService';
import {
  ensureMovementSession,
  getMovementStatus,
  stopMovement,
  type MovementStatus,
  type MovementStatusResponse,
} from '../services/agentMovementService';
import { passkeyKitService } from '../services/passkeyKitService';
import {
  authorizeEntryWithKeypair,
  findAuthEntryIndexForAddress,
} from '../services/sorobanAuthCredentials';

interface AgentsTabProps {
  /** The profile we're viewing — the operator wallet for this tab. */
  subjectWallet: string;
  /**
   * True when the connected viewer is the same wallet as `subjectWallet`.
   * The tab only renders for owners (gated at the parent), but we keep
   * this prop so the empty-state copy can speak in second-person.
   */
  isOwner: boolean;
  /**
   * Payment handler injected by the host (App.tsx) — same one
   * passed to the human ProfileOverlay. Threaded through to
   * AgentLiveBroadcastModal so an agent broadcast that requires
   * payment can drive the operator's wallet inline.
   */
  onExecuteLivePayment?: ExecuteLivePayment;
  /** Freighter / WalletConnect — signs `register_agent` when operator is a `G…` address. */
  stellarWallet?: SigningWallet | null;
  /** Passkey smart wallet — signs when operator is a `C…` contract address. */
  passkey?: PasskeySigner | null;
  /**
   * Viewer-resolved ISO numeric country for paid WebRTC policy (same as the
   * Live tab). When set, agent go-live uses this instead of relying only on
   * on-chain agent residency (needed for wallet-as-agent / no citizenship).
   */
  viewerBroadcastCountry?: number | null;
  /** Optional ~11 km quantised location for the session request (parity with human live). */
  approxLocation?: ApproxLocation;
  /** Open another wallet/agent's profile overlay. Used by the agent
   *  detail panel's "Open profile ↗" so the operator can jump to an
   *  agent's profile (and its past broadcasts). */
  onOpenWallet?: (wallet: string) => void;
}

/** Classic account (`G…`) or Soroban contract (`C…`) — registry accepts both as agent `Address`. */
const STELLAR_ADDRESS = /^[GC][A-Z2-7]{55}$/;

function resolveNetworkPassphrase(): string {
  return process.env.REACT_APP_STELLAR_NETWORK_PASSPHRASE || Networks.TESTNET;
}

const AgentsTab: React.FC<AgentsTabProps> = ({
  subjectWallet,
  isOwner,
  onExecuteLivePayment,
  stellarWallet,
  passkey,
  viewerBroadcastCountry,
  approxLocation,
  onOpenWallet,
}) => {
  const [agents, setAgents] = useState<CachedAgentRecord[]>([]);
  const [schema, setSchema] = useState<CapabilityEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  /**
   * Inline registration drawer toggle. The previous layout buried the
   * "Register agent" panel below every other section once at least one
   * agent existed, so operators had to scroll past the entire detail
   * pane to add another. We surface it as an explicit, always-visible
   * toolbar action and render the form inline above the dashboard
   * grid when toggled — same component, far less hunting.
   */
  const [showRegister, setShowRegister] = useState(false);
  const [globeFullscreen, setGlobeFullscreen] = useState(false);
  // Per-agent live movement-loop status, keyed by agent address.
  // Polled in a single batch every 5 s so the rail cards can show a
  // running/paused pill (and the toolbar can render "Stop all
  // running") without forcing the operator to open each agent.
  const [movementByAgent, setMovementByAgent] = useState<Record<string, MovementStatusResponse>>({});
  const [stopAllBusy, setStopAllBusy] = useState(false);
  const [stopAllError, setStopAllError] = useState<string | null>(null);
  const [teleportTarget, setTeleportTarget] = useState<CachedAgentRecord | null>(null);
  const [detailRefreshToken, setDetailRefreshToken] = useState(0);
  // "Sync agent by address" recovery affordance — recovers an agent
  // that's registered on-chain under this profile but missing from the
  // operator index (post-submit read lag, or a sibling instance).
  const [showSync, setShowSync] = useState(false);
  const [syncAddr, setSyncAddr] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // Per-agent on-chain reconciliation state. The list is served from the
  // off-chain operator index, which survives a registry redeploy/testnet
  // reset even though the agent no longer exists on the *active* registry
  // contract. We probe chain-truth for each listed agent so we can flag
  // the "in your list but not on-chain" (stale) ones and nudge a
  // re-register — otherwise movement just fails with `agent_not_registered`.
  const [onChainStatus, setOnChainStatus] = useState<
    Record<string, 'onchain' | 'stale' | 'mismatch'>
  >({});

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, sch] = await Promise.all([
        listAgents({ operator: subjectWallet }).catch(() => []),
        getCapabilitySchema().catch(() => null),
      ]);
      setAgents(list);
      setSchema(sch ? sch.capabilities : null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [subjectWallet]);

  useEffect(() => { void refresh(); }, [refresh]);

  /**
   * Reconcile a just-registered agent into the list. The backend already
   * retries the chain read on /submit, but we belt-and-braces it here:
   * poll `syncAgent` a few times (closes any residual read-lag), insert
   * the record optimistically when it belongs to this profile, then
   * refresh the canonical list.
   */
  const reconcileAfterRegister = useCallback(async (agentAddress: string) => {
    let synced: AgentRecord | null = null;
    for (let i = 0; i < 4; i += 1) {
      try {
        synced = await syncAgent(agentAddress);
      } catch { synced = null; }
      if (synced) break;
      await new Promise((r) => setTimeout(r, 1500));
    }
    if (synced && synced.operator === subjectWallet) {
      setAgents((prev) => (
        prev.some((a) => a.address === synced!.address)
          ? prev
          : [...prev, {
              address:      synced!.address,
              operator:     synced!.operator || subjectWallet,
              kind:         synced!.kind,
              kindLabel:    synced!.kindLabel,
              capabilities: synced!.capabilities,
              status:       synced!.statusLabel,
              statusCode:   synced!.status,
            }]
      ));
    }
    await refresh();
  }, [subjectWallet, refresh]);

  const handleSyncByAddress = useCallback(async () => {
    const addr = syncAddr.trim();
    setSyncMsg(null);
    if (!STELLAR_ADDRESS.test(addr)) {
      setSyncMsg({ kind: 'err', text: 'Enter a valid G… or C… address (56 characters).' });
      return;
    }
    setSyncBusy(true);
    try {
      const rec = await syncAgent(addr);
      if (!rec) {
        setSyncMsg({ kind: 'err', text: 'That address is not registered as an agent on-chain.' });
      } else if (rec.operator !== subjectWallet) {
        setSyncMsg({
          kind: 'err',
          text: `That agent's operator is ${rec.operator ? `${rec.operator.slice(0, 6)}…${rec.operator.slice(-4)}` : 'unknown'}, not this profile, so it won't appear here.`,
        });
      } else {
        setSyncMsg({ kind: 'ok', text: 'Synced. The agent now appears in your list.' });
        setSyncAddr('');
        await refresh();
      }
    } catch (err: any) {
      setSyncMsg({ kind: 'err', text: err?.message || 'Sync failed.' });
    } finally {
      setSyncBusy(false);
    }
  }, [syncAddr, subjectWallet, refresh]);

  // Batch-poll movement status for the visible agent set so each rail
  // card can render a running / paused / stopped pill. We deliberately
  // poll all-at-once (5s cadence) rather than letting each rail card
  // poll independently — keeps the request count proportional to the
  // fleet size instead of multiplying it. Pauses while the tab is
  // hidden so a backgrounded profile doesn't drum on GeoLink.
  useEffect(() => {
    if (agents.length === 0) {
      setMovementByAgent({});
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const results = await Promise.all(
          agents.map((a) =>
            getMovementStatus(a.address)
              .then((s) => [a.address, s] as const)
              .catch(() => [a.address, null] as const),
          ),
        );
        if (cancelled) return;
        setMovementByAgent((prev) => {
          const next: Record<string, MovementStatusResponse> = { ...prev };
          for (const [addr, status] of results) {
            if (status) next[addr] = status;
          }
          for (const k of Object.keys(next)) {
            if (!agents.find((a) => a.address === k)) delete next[k];
          }
          return next;
        });
      } catch { /* ignore poll errors */ }
    };
    void tick();
    const id = setInterval(tick, 5_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [agents]);

  // Verify each listed agent against the *active* on-chain registry so we
  // can surface stale entries (present in the index but not on-chain — e.g.
  // after a registry redeploy / testnet reset). Fails open: a transient
  // chain/RPC error is treated as "onchain" so we never cry wolf.
  useEffect(() => {
    if (agents.length === 0) { setOnChainStatus({}); return; }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        agents.map(async (a) => {
          try {
            const rec = await getAgent(a.address);
            if (!rec) return [a.address, 'stale'] as const;
            if (rec.operator && rec.operator !== subjectWallet) {
              return [a.address, 'mismatch'] as const;
            }
            return [a.address, 'onchain'] as const;
          } catch {
            return [a.address, 'onchain'] as const;
          }
        }),
      );
      if (cancelled) return;
      setOnChainStatus(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [agents, subjectWallet]);

  const staleAddresses = useMemo(
    () => new Set(
      Object.entries(onChainStatus)
        .filter(([, s]) => s === 'stale' || s === 'mismatch')
        .map(([addr]) => addr),
    ),
    [onChainStatus],
  );

  const staleAgents = useMemo(
    () => agents.filter((a) => staleAddresses.has(a.address)),
    [agents, staleAddresses],
  );

  const runningAgents = useMemo(
    () => agents.filter((a) => {
      const s = movementByAgent[a.address];
      return s && (s.status === 'running' || s.status === 'paused');
    }),
    [agents, movementByAgent],
  );

  const handleStopAll = useCallback(async () => {
    if (!isOwner || runningAgents.length === 0 || stopAllBusy) return;
    if (typeof window !== 'undefined'
      && !window.confirm(`Stop the movement loop for ${runningAgents.length} agent${runningAgents.length === 1 ? '' : 's'}?`)) {
      return;
    }
    setStopAllError(null);
    setStopAllBusy(true);
    try {
      const signers = { stellarWallet: stellarWallet ?? null, passkey: passkey ?? null };
      const results = await Promise.allSettled(
        runningAgents.map(async (a) => {
          // Each agent needs its own operator session token. Re-using
          // ensureMovementSession means a stale token is auto-re-minted
          // by the next call's 401 retry (see authedFetch).
          await ensureMovementSession({
            operator:      subjectWallet,
            agent:         a.address,
            stellarWallet: stellarWallet ?? null,
            passkey:       passkey ?? null,
          });
          return stopMovement(subjectWallet, a.address, signers);
        }),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        const firstReason = (failed[0] as PromiseRejectedResult).reason;
        setStopAllError(
          `Stopped ${results.length - failed.length} of ${results.length}. ${firstReason?.message || firstReason || 'See console.'}`,
        );
      }
      // Optimistically clear the polled status so the pills snap to
      // "stopped" without waiting for the next 5 s poll. The next
      // poll will overwrite this anyway.
      setMovementByAgent((prev) => {
        const next = { ...prev };
        for (const a of runningAgents) {
          if (next[a.address]) {
            next[a.address] = { ...next[a.address], status: 'stopped' };
          }
        }
        return next;
      });
    } catch (err: any) {
      setStopAllError(err?.message || 'Failed to stop agents');
    } finally {
      setStopAllBusy(false);
    }
  }, [isOwner, runningAgents, stopAllBusy, subjectWallet, stellarWallet, passkey]);

  const selectedAgent = useMemo(
    () => agents.find((a) => a.address === selected) || null,
    [agents, selected],
  );

  const activeCount = useMemo(
    () => agents.filter((a) => a.status !== 'revoked').length,
    [agents],
  );

  // Renderer the fullscreen globe overlays so we can show the same
  // agent rail + detail panel inside it. Defined as a ref-stable
  // closure so AgentsMiniGlobe doesn't have to know our internal
  // shape.
  const renderFullscreenSidePanel = useCallback(() => (
    <FullscreenSidePanel
      agents={agents}
      schema={schema}
      movementByAgent={movementByAgent}
      staleAddresses={staleAddresses}
      selectedAddress={selected}
      onSelect={(addr) => setSelected(addr === selected ? null : addr)}
      onTeleport={isOwner ? (addr) => {
        const a = agents.find((x) => x.address === addr) || null;
        if (a) setTeleportTarget(a);
      } : undefined}
      detail={selectedAgent ? (
        <>
          <AgentDetailPanel
            agent={selectedAgent}
            operatorWallet={isOwner ? subjectWallet : null}
            stellarWallet={stellarWallet ?? null}
            passkey={passkey ?? null}
            onExecuteLivePayment={onExecuteLivePayment}
            onClose={() => setSelected(null)}
            onRevoked={() => {
              setSelected(null);
              void refresh();
            }}
            viewerBroadcastCountry={viewerBroadcastCountry}
            approxLocation={approxLocation}
            detailRefreshToken={detailRefreshToken}
            onResidencyChanged={() => setDetailRefreshToken((t) => t + 1)}
            onOpenWallet={onOpenWallet}
          />
          <AgentGrantedCapabilitiesPanel
            agent={selectedAgent}
            schema={schema}
            operatorWallet={isOwner ? subjectWallet : null}
            stellarWallet={stellarWallet ?? null}
            passkey={passkey ?? null}
            onUpdated={() => void refresh()}
          />
        </>
      ) : (
        <div style={emptyDetailHintStyle}>
          Pick an agent on the map or in the list to manage its capabilities
          and movement loop here.
        </div>
      )}
    />
  ), [
    agents, selected, selectedAgent, schema, isOwner, subjectWallet,
    stellarWallet, passkey, onExecuteLivePayment, viewerBroadcastCountry,
    approxLocation, refresh, movementByAgent, staleAddresses,
    detailRefreshToken, onOpenWallet,
  ]);

  return (
    <div style={tabRoot}>
      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <header style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, minWidth: 0 }}>
          <h2 style={titleStyle}>🤖 AI Agents</h2>
          {agents.length > 0 && (
            <span style={countPillStyle}>
              {activeCount} active{agents.length !== activeCount ? ` · ${agents.length - activeCount} revoked` : ''}
            </span>
          )}
        </div>
        <div style={toolbarActionsStyle}>
          {isOwner && runningAgents.length > 0 && (
            <button
              type="button"
              onClick={() => void handleStopAll()}
              disabled={stopAllBusy}
              style={stopAllBtnStyle}
              title={`Stop the movement loop for ${runningAgents.length} agent${runningAgents.length === 1 ? '' : 's'} (signs once per agent).`}
            >
              {stopAllBusy ? 'Stopping…' : `■ Stop all (${runningAgents.length})`}
            </button>
          )}
          <button
            onClick={() => void refresh()}
            style={refreshBtnStyle}
            disabled={loading}
            title="Re-pull from chain"
          >
            {loading ? '…' : 'Refresh'}
          </button>
          {isOwner && (
            <button
              type="button"
              onClick={() => { setShowSync((v) => !v); setSyncMsg(null); }}
              style={showSync ? toolbarBtnStyleActive : refreshBtnStyle}
              aria-expanded={showSync}
              title="Recover an agent that's registered on-chain under this profile but missing from the list."
            >
              {showSync ? 'Close sync' : 'Sync by address'}
            </button>
          )}
          {isOwner && schema && schema.length > 0 && (
            <button
              type="button"
              onClick={() => setShowRegister((v) => !v)}
              style={showRegister ? toolbarBtnStyleActive : toolbarBtnStylePrimary}
              aria-expanded={showRegister}
            >
              {showRegister ? 'Close registration' : '＋ Register agent'}
            </button>
          )}
        </div>
      </header>

      {/* ── Sync agent by address (recovery) ───────────────────────────── */}
      {isOwner && showSync && (
        <div style={syncPanelStyle}>
          <div style={{ fontSize: 13, color: '#374151', marginBottom: 8 }}>
            Registered an agent that isn't showing? Paste its address to re-pull it
            from chain into your list.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="text"
              value={syncAddr}
              onChange={(e) => { setSyncAddr(e.target.value.trim()); setSyncMsg(null); }}
              placeholder="G… or C… agent address"
              style={{ ...inputStyle, flex: '1 1 320px', marginBottom: 0 }}
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={() => void handleSyncByAddress()}
              disabled={syncBusy || !STELLAR_ADDRESS.test(syncAddr.trim())}
              style={toolbarBtnStylePrimary}
            >
              {syncBusy ? 'Syncing…' : 'Sync'}
            </button>
          </div>
          {syncMsg && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: syncMsg.kind === 'ok' ? '#047857' : '#b91c1c',
              }}
            >
              {syncMsg.text}
            </div>
          )}
        </div>
      )}

      {stopAllError && (
        <div style={errorBoxStyle}>
          <strong>Stop all:</strong> {stopAllError}
        </div>
      )}

      <p style={subTitleStyle}>
        {isOwner ? (
          'Register many agents — each needs its own G… or C… address. They appear on the globe and can use MCP tools you allow; spending is capped by on-chain policy. Revoke removes active status (the same address cannot be registered again).'
        ) : (
          <>
            AI agents this wallet operates on-chain. You're viewing in
            read-only mode — explore the fleet on the globe, inspect each
            agent's granted capabilities, and preview what their broadcasts
            look like. Live broadcasts and on-chain edits are reserved for
            the operator.
          </>
        )}
      </p>

      {error && (
        <div style={errorBoxStyle}>
          <strong>Couldn't load agents.</strong> {error}
        </div>
      )}

      {/* ── Stale agent nudge (listed but not on the active registry) ───── */}
      {isOwner && staleAgents.length > 0 && (
        <div style={staleBannerStyle}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            ⚠ {staleAgents.length} agent{staleAgents.length === 1 ? '' : 's'} not on the current registry
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.45, marginBottom: 8 }}>
            {staleAgents.length === 1 ? 'This agent is' : 'These agents are'} in your
            list but {staleAgents.length === 1 ? "isn't" : "aren't"} registered on the
            active registry contract — so movement and on-chain actions will fail with
            {' '}<code style={inlineCodeStyle}>agent_not_registered</code>. This usually
            happens after a registry redeploy or testnet reset.
            Re-register {staleAgents.length === 1 ? 'it' : 'them'} (the same address is fine)
            to use {staleAgents.length === 1 ? 'it' : 'them'} again.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {staleAgents.map((a) => (
              <code key={a.address} style={inlineCodeStyle} title={a.address}>
                {shortAddr(a.address)}
              </code>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowRegister(true)}
            style={toolbarBtnStylePrimary}
          >
            ＋ Re-register
          </button>
        </div>
      )}

      {/* ── Inline registration drawer ─────────────────────────────────── */}
      {isOwner && showRegister && schema && schema.length > 0 && (
        <AgentRegistrationPanel
          subjectWallet={subjectWallet}
          schema={schema}
          stellarWallet={stellarWallet ?? null}
          passkey={passkey ?? null}
          onSuccess={(agentAddress) => {
            setShowRegister(false);
            void reconcileAfterRegister(agentAddress);
          }}
          panelTitle={agents.length === 0 ? 'Register your first agent' : 'Register another agent'}
        />
      )}

      {/* ── Globe + agent rail dashboard ───────────────────────────────── */}
      <div style={dashboardStyle}>
        <AgentsMiniGlobe
          agents={agents}
          selectedAddress={selected}
          onSelect={(addr) => setSelected(addr === selected ? null : addr)}
          variant="compact"
          onToggleFullscreen={() => setGlobeFullscreen(true)}
        />

        {!loading && agents.length === 0 && !error && (
          <EmptyState isOwner={isOwner} schema={schema} />
        )}

        {agents.length > 0 && (
          <div style={agentRailStyle}>
            {agents.map((a) => (
              <AgentRailCard
                key={a.address}
                agent={a}
                schema={schema}
                movementStatus={movementByAgent[a.address] || null}
                active={selected === a.address}
                stale={staleAddresses.has(a.address)}
                onSelect={() => setSelected(a.address === selected ? null : a.address)}
                onTeleport={isOwner ? () => setTeleportTarget(a) : undefined}
              />
            ))}
          </div>
        )}

        {selectedAgent && (
          <AgentDetailPanel
            agent={selectedAgent}
            operatorWallet={isOwner ? subjectWallet : null}
            stellarWallet={stellarWallet ?? null}
            passkey={passkey ?? null}
            onExecuteLivePayment={onExecuteLivePayment}
            onClose={() => setSelected(null)}
            onRevoked={() => {
              setSelected(null);
              void refresh();
            }}
            viewerBroadcastCountry={viewerBroadcastCountry}
            approxLocation={approxLocation}
            detailRefreshToken={detailRefreshToken}
            onResidencyChanged={() => setDetailRefreshToken((t) => t + 1)}
            onOpenWallet={onOpenWallet}
          />
        )}
      </div>

      {selectedAgent && (
        <AgentGrantedCapabilitiesPanel
          agent={selectedAgent}
          schema={schema}
          operatorWallet={isOwner ? subjectWallet : null}
          stellarWallet={stellarWallet ?? null}
          passkey={passkey ?? null}
          onUpdated={() => void refresh()}
        />
      )}

      {schema && (
        <CapabilitySchemaPreview capabilities={schema} />
      )}

      {globeFullscreen && (
        <AgentsMiniGlobe
          agents={agents}
          selectedAddress={selected}
          onSelect={(addr) => setSelected(addr === selected ? null : addr)}
          variant="fullscreen"
          onToggleFullscreen={() => setGlobeFullscreen(false)}
          renderSidePanel={renderFullscreenSidePanel}
        />
      )}

      {isOwner && teleportTarget && (
        <AgentTeleportModal
          agent={teleportTarget}
          operatorWallet={subjectWallet}
          stellarWallet={stellarWallet ?? null}
          passkey={passkey ?? null}
          defaultCountry={
            (movementByAgent[teleportTarget.address]?.country
              || null) as string | null
          }
          onClose={() => setTeleportTarget(null)}
          onTeleported={() => {
            const addr = teleportTarget?.address;
            if (addr) {
              void getMovementStatus(addr)
                .then((s) => setMovementByAgent((prev) => ({ ...prev, [addr]: s })))
                .catch(() => {});
            }
            setDetailRefreshToken((t) => t + 1);
          }}
        />
      )}
    </div>
  );
};

// ── Fullscreen side panel ─────────────────────────────────────────────────
//
// Same agents-rail-then-detail layout the docked dashboard uses, but
// stacked vertically and scrollable so it fits inside the fullscreen
// globe overlay without fighting for horizontal space.

const FullscreenSidePanel: React.FC<{
  agents: CachedAgentRecord[];
  schema: CapabilityEntry[] | null;
  movementByAgent: Record<string, MovementStatusResponse>;
  staleAddresses?: Set<string>;
  selectedAddress: string | null;
  onSelect: (address: string) => void;
  onTeleport?: (address: string) => void;
  detail: React.ReactNode;
}> = ({ agents, schema, movementByAgent, staleAddresses, selectedAddress, onSelect, onTeleport, detail }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.6 }}>
        Fleet ({agents.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {agents.map((a) => (
          <AgentRailCard
            key={a.address}
            agent={a}
            schema={schema}
            movementStatus={movementByAgent[a.address] || null}
            active={selectedAddress === a.address}
            stale={staleAddresses?.has(a.address) ?? false}
            onSelect={() => onSelect(a.address)}
            onTeleport={onTeleport ? () => onTeleport(a.address) : undefined}
          />
        ))}
      </div>
      <div style={{ marginTop: 4 }}>
        {detail}
      </div>
    </div>
  );
};

// ── Agent rail card ───────────────────────────────────────────────────────
//
// Replaces the old vertical row list — these compact cards arrange
// horizontally (or wrap to multiple rows) so the agent picker doesn't
// eat the whole left half of the dashboard, and the detail / movement
// loop panel below gets the full width it needs.

const AgentRailCard: React.FC<{
  agent: CachedAgentRecord;
  schema: CapabilityEntry[] | null;
  movementStatus: MovementStatusResponse | null;
  active: boolean;
  /** True when the agent is in the operator index but not on the active
   *  on-chain registry (e.g. after a registry redeploy) — flagged so the
   *  operator knows movement will fail until they re-register. */
  stale?: boolean;
  onSelect: () => void;
  /** If set, the card shows a "Teleport" quick action that opens
   *  the operator's place-search modal for this agent. */
  onTeleport?: () => void;
}> = ({ agent, schema, movementStatus, active, stale = false, onSelect, onTeleport }) => {
  const revoked = agent.status === 'revoked';
  const capIndex = useMemo(() => {
    const m = new Map<string, CapabilityEntry>();
    (schema || []).forEach((c) => m.set(c.id, c));
    return m;
  }, [schema]);
  const capIds = agent.capabilities || [];
  const visibleCaps = capIds.slice(0, 4);
  const extraCaps = capIds.length - visibleCaps.length;
  const movement: MovementStatus | null = movementStatus?.status ?? null;
  const lastHopName = movementStatus?.lastWaypoint?.name || null;

  return (
    <div
      style={{
        ...railCardStyle,
        ...(active ? railCardActiveStyle : null),
        ...(revoked ? railCardRevokedStyle : null),
        cursor: 'pointer',
      }}
      title={agent.address}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
        <span aria-hidden="true" style={{ fontSize: 18 }}>🤖</span>
        <div style={{ minWidth: 0, textAlign: 'left' }}>
          <div style={railCardTitleStyle}>
            {shortAddr(agent.address)}
            {agent.kindLabel && agent.kindLabel !== 'generic' && (
              <span style={kindPill}>{agent.kindLabel}</span>
            )}
          </div>
          <div style={railCardMetaStyle}>
            {capIds.length === 0
              ? 'No capabilities granted'
              : `${capIds.length} granted capabilit${capIds.length === 1 ? 'y' : 'ies'}`}
            {revoked ? ' · revoked' : ''}
          </div>
          {visibleCaps.length > 0 && (
            <div style={railCapChipsStyle}>
              {visibleCaps.map((id) => {
                const meta = capIndex.get(id);
                return (
                  <span key={id} style={railCapChipStyle} title={meta?.description || id}>
                    {meta?.label || id}
                  </span>
                );
              })}
              {extraCaps > 0 && (
                <span style={railCapChipMoreStyle}>+{extraCaps}</span>
              )}
            </div>
          )}
          {!revoked && movement && movement !== 'stopped' && (
            <div style={railMovementMetaStyle} title={
              lastHopName
                ? `Last hop: ${lastHopName}`
                : movementStatus?.country
                  ? `Country: ${movementStatus.country}`
                  : ''
            }>
              {movement === 'running' ? '▶ moving' : '⏸ paused'}
              {lastHopName ? ` · ${lastHopName.length > 24 ? lastHopName.slice(0, 22) + '…' : lastHopName}` : ''}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {stale && !revoked && (
          <span
            style={stalePillStyle}
            title="In your list but not on the active on-chain registry — re-register to use this agent again."
          >
            ⚠ re-register
          </span>
        )}
        {onTeleport && !revoked && !stale && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onTeleport(); }}
            style={railTeleportBtnStyle}
            title="Teleport this agent to a place"
          >
            ✈ Teleport
          </button>
        )}
        <span style={{
          ...railCardBadgeStyle,
          background: revoked
            ? '#fee2e2'
            : movement === 'running' ? '#dcfce7'
            : movement === 'paused'  ? '#fef3c7'
            : '#e5e7eb',
          color: revoked
            ? '#991b1b'
            : movement === 'running' ? '#166534'
            : movement === 'paused'  ? '#92400e'
            : '#374151',
          borderColor: revoked
            ? '#fecaca'
            : movement === 'running' ? '#bbf7d0'
            : movement === 'paused'  ? '#fde68a'
            : '#d1d5db',
        }}>
          {revoked
            ? 'revoked'
            : movement === 'running' ? 'running'
            : movement === 'paused'  ? 'paused'
            : 'active'}
        </span>
      </div>
    </div>
  );
};

// ── Granted capabilities (on-chain, public read) ───────────────────────────
//
// Collapsible panel shown when an agent is selected — sits above the MCP
// tool reference. Visitors see the same grant set as the operator; only
// the operator can edit when their wallet is connected.

const AgentGrantedCapabilitiesPanel: React.FC<{
  agent: CachedAgentRecord;
  schema: CapabilityEntry[] | null;
  operatorWallet: string | null;
  stellarWallet: SigningWallet | null;
  passkey: PasskeySigner | null;
  onUpdated?: () => void;
}> = ({
  agent,
  schema,
  operatorWallet,
  stellarWallet,
  passkey,
  onUpdated,
}) => {
  const [chainRecord, setChainRecord] = useState<AgentRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingCaps, setEditingCaps] = useState<Set<string> | null>(null);
  const [capsBusy, setCapsBusy] = useState(false);
  const [capsErr, setCapsErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rec = await getAgent(agent.address).catch(() => null);
      setChainRecord(rec);
    } finally {
      setLoading(false);
    }
  }, [agent.address]);

  useEffect(() => { void load(); }, [load]);

  const capabilityIndex = useMemo(() => {
    const m = new Map<string, CapabilityEntry>();
    (schema || []).forEach((c) => m.set(c.id, c));
    return m;
  }, [schema]);

  const granted = chainRecord?.capabilities ?? agent.capabilities ?? [];
  const statusLabel = (chainRecord?.statusLabel || agent.status) as string;
  const isRevoked = statusLabel === 'revoked';
  const opIsClassic = operatorWallet?.startsWith('G') ?? false;
  const canSign = operatorWallet
    ? (opIsClassic ? !!stellarWallet : !!passkey)
    : false;

  const handleSaveCapabilities = useCallback(async () => {
    if (!operatorWallet || isRevoked || !canSign || !editingCaps) return;
    setCapsErr(null);
    setCapsBusy(true);
    try {
      const desired = Array.from(editingCaps).sort();
      const built = await buildSetCapabilitiesTx({
        operator:     operatorWallet,
        agent:        agent.address,
        capabilities: desired,
      });
      let signedXdr: string;
      if (opIsClassic) {
        signedXdr = await stellarWallet!.signTransaction(built.xdr, resolveNetworkPassphrase());
      } else {
        signedXdr = await passkey!.signUnsignedXdr(built.xdr, passkey!.account, built.latestLedger);
      }
      await submitSignedAgentTx(signedXdr, { agentToRefresh: agent.address });
      setEditingCaps(null);
      await load();
      onUpdated?.();
    } catch (e: unknown) {
      setCapsErr(humanizeUnknownError(e));
    } finally {
      setCapsBusy(false);
    }
  }, [
    agent.address,
    canSign,
    editingCaps,
    isRevoked,
    load,
    onUpdated,
    opIsClassic,
    operatorWallet,
    passkey,
    stellarWallet,
  ]);

  const capCount = editingCaps ? editingCaps.size : granted.length;

  return (
    <section style={{ ...sectionStyle, marginTop: 12 }}>
      <details style={collapsibleDetailsStyle}>
        <summary style={collapsibleSummaryStyle}>
          Granted capabilities ({capCount})
          <span style={collapsibleSummaryHintStyle}>
            {shortAddr(agent.address)}
          </span>
        </summary>
        <p style={collapsibleIntroStyle}>
          On-chain capabilities granted via{' '}
          <code style={inlineCodeStyle}>set_capabilities</code>. Anyone can
          inspect this list — it reflects what MCP tools the operator has
          allowed this agent to use.
          {loading && ' Refreshing from chain…'}
        </p>

        <div style={capsHeaderStyle}>
          {operatorWallet && !isRevoked && schema && schema.length > 0 && (
            editingCaps ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => { setEditingCaps(null); setCapsErr(null); }}
                  disabled={capsBusy}
                  style={capsCancelBtnStyle}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleSaveCapabilities()}
                  disabled={capsBusy || !canSign}
                  style={capsSaveBtnStyle}
                  title={canSign
                    ? 'Sign set_capabilities with your operator wallet'
                    : 'Connect your wallet (Freighter or passkey) to sign'}
                >
                  {capsBusy ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditingCaps(new Set(granted))}
                disabled={!canSign}
                style={capsEditBtnStyle}
                title={canSign
                  ? 'Grant or revoke capabilities on-chain'
                  : 'Connect your wallet (Freighter or passkey) to edit'}
              >
                ✎ Edit capabilities
              </button>
            )
          )}
        </div>

        {capsErr && (
          <div style={{ ...errorBoxStyle, marginBottom: 8 }}>{capsErr}</div>
        )}

        {editingCaps ? (
          schema && schema.length > 0 ? (
            <ul style={capGridStyle}>
              {schema.map((meta) => {
                const checked = editingCaps.has(meta.id);
                const isNew  = checked && !granted.includes(meta.id);
                const wasOn  = granted.includes(meta.id);
                const dropped = wasOn && !checked;
                return (
                  <li
                    key={meta.id}
                    style={{
                      ...capItemStyle,
                      borderColor: isNew
                        ? 'rgba(34,197,94,0.55)'
                        : dropped ? 'rgba(244,114,182,0.55)' : (capItemStyle.borderColor as any),
                      background: isNew
                        ? 'rgba(34,197,94,0.06)'
                        : dropped ? 'rgba(244,114,182,0.05)' : (capItemStyle.background as any),
                    }}
                  >
                    <label style={{ display: 'flex', gap: 10, cursor: 'pointer', alignItems: 'flex-start' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={capsBusy}
                        onChange={(e) => {
                          setEditingCaps((prev) => {
                            const next = new Set(prev || []);
                            if (e.target.checked) next.add(meta.id);
                            else next.delete(meta.id);
                            return next;
                          });
                        }}
                        style={{ marginTop: 3 }}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                          <code style={capIdStyle}>{meta.id}</code>
                          <span style={capLabelStyle}>{meta.label}</span>
                          {isNew  && <span style={capBadgeAddStyle}>+ adding</span>}
                          {dropped && <span style={capBadgeRemoveStyle}>− removing</span>}
                        </div>
                        <div style={capDescStyle}>{meta.description}</div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              Capability schema is not available — try refreshing.
            </div>
          )
        ) : granted.length === 0 ? (
          <div style={{ color: '#6b7280', fontSize: 13 }}>
            No capabilities granted yet.{' '}
            {operatorWallet && !isRevoked && canSign
              ? 'Click "Edit capabilities" to grant some on-chain.'
              : 'The operator can grant them via MCP or the profile editor.'}
          </div>
        ) : (
          <ul style={capGridStyle}>
            {granted.map((id) => {
              const meta = capabilityIndex.get(id);
              return (
                <li key={id} style={capItemStyle}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <code style={capIdStyle}>{id}</code>
                    {meta && <span style={capLabelStyle}>{meta.label}</span>}
                    {meta?.writes_chain && <span style={chainTagStyle}>Stellar write</span>}
                    {meta && meta.cost_xlm > 0 && <span style={costTagStyle}>{meta.cost_xlm} XLM</span>}
                  </div>
                  {meta && <div style={capDescStyle}>{meta.description}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </details>
    </section>
  );
};

// ── Residency change form (Case A passkey agents) ─────────────────────────

const RESIDENCY_STEP_LABELS: Record<ResidencyChainStep, string> = {
  relinquish: 'Relinquishing old residency…',
  apply:      'Applying for new residency…',
  confirm:    'Paying fee & confirming…',
  poll:       'Waiting for chain to settle…',
};

const ResidencyChangeForm: React.FC<{
  targetCountry: string;
  onTargetCountryChange: (v: string) => void;
  busy: boolean;
  chainStep: ResidencyChainStep | null;
  error: string | null;
  submitLabel: string;
  onSubmit: () => void;
  currentCountry?: string;
}> = ({
  targetCountry,
  onTargetCountryChange,
  busy,
  chainStep,
  error,
  submitLabel,
  onSubmit,
  currentCountry,
}) => {
  const target = targetCountry.toUpperCase();
  const sameAsCurrent = !!(currentCountry && target === currentCountry.toUpperCase());

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={targetCountry}
          onChange={(e) => onTargetCountryChange(e.target.value.toUpperCase().slice(0, 2))}
          placeholder="AE"
          maxLength={2}
          disabled={busy}
          aria-label="Target country ISO-2"
          style={residencyInputStyle}
        />
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy || target.length !== 2 || sameAsCurrent}
          style={residencyBtnStyle}
        >
          {busy ? 'Working…' : submitLabel}
        </button>
      </div>
      {busy && chainStep && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
          {RESIDENCY_STEP_LABELS[chainStep]}
        </div>
      )}
      {error && (
        <div style={{ fontSize: 12, color: '#991b1b', marginTop: 6 }}>{error}</div>
      )}
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
        Pays ~1 USDC from the agent wallet. Use Teleport to change country and move in one step.
      </div>
    </div>
  );
};

// ── Detail panel ───────────────────────────────────────────────────────────

const AgentDetailPanel: React.FC<{
  agent: CachedAgentRecord;
  /** Connected operator wallet — passed to AgentLiveBroadcastModal so the
   *  paid live-session can be requested under the operator's identity. */
  operatorWallet: string | null;
  stellarWallet: SigningWallet | null;
  passkey: PasskeySigner | null;
  /** Wallet payment handler — see AgentsTabProps. */
  onExecuteLivePayment?: ExecuteLivePayment;
  onClose: () => void;
  /** After successful on-chain revoke_agent. */
  onRevoked?: () => void;
  viewerBroadcastCountry?: number | null;
  approxLocation?: ApproxLocation;
  /** Bump to force a reload (e.g. after teleport from another panel). */
  detailRefreshToken?: number;
  /** After a browser-side residency chain completes. */
  onResidencyChanged?: () => void;
  /** Open the agent's own profile overlay (→ its past broadcasts). */
  onOpenWallet?: (wallet: string) => void;
}> = ({
  agent,
  operatorWallet,
  stellarWallet,
  passkey,
  onExecuteLivePayment,
  onClose,
  onRevoked,
  viewerBroadcastCountry,
  approxLocation,
  detailRefreshToken = 0,
  onResidencyChanged,
  onOpenWallet,
}) => {
  const [chainRecord, setChainRecord] = useState<AgentRecord | null>(null);
  const [policy, setPolicy] = useState<AgentPolicySnapshot | null>(null);
  const [residency, setResidency] = useState<ResidencySnapshot | null>(null);
  const [profile,   setProfile]   = useState<ProfileRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [liveOpen,    setLiveOpen]    = useState(false);
  const [revokeBusy,  setRevokeBusy]  = useState(false);
  const [revokeErr,   setRevokeErr]   = useState<string | null>(null);
  const [disposeOpen, setDisposeOpen] = useState(false);
  const [disposeInstructions, setDisposeInstructions] = useState<DisposeParams | null>(null);
  const [targetCountry, setTargetCountry] = useState('');
  const [residencyBusy, setResidencyBusy] = useState(false);
  const [residencyChainErr, setResidencyChainErr] = useState<string | null>(null);
  const [residencyChainStep, setResidencyChainStep] = useState<ResidencyChainStep | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const [rec, pol, res, prof] = await Promise.all([
        getAgent(agent.address).catch(() => null),
        getAgentPolicy(agent.address).catch(() => null),
        getResidencySnapshot(agent.address).catch(() => null),
        // Hydrate the agent's geo-profile so the broadcast canvas
        // can show the agent's own avatar / display name instead of
        // the procedural fallback. Failure is non-fatal — many agent
        // addresses (esp. fresh G… keys) won't have a profile written
        // yet, and the canvas falls back gracefully.
        getPublicProfile(agent.address).catch(() => null),
      ]);
      setChainRecord(rec);
      setPolicy(pol);
      setResidency(res);
      setProfile(prof);
    } catch (e: any) {
      setErr(e?.message || 'Failed to read on-chain state');
    } finally {
      setBusy(false);
    }
  }, [agent.address]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (detailRefreshToken > 0) void load();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- token bump only
  }, [detailRefreshToken]);

  // Profile fields handed to the broadcast modals. Empty strings on
  // the geo-profile contract mean "unset" — convert to null so the
  // canvas falls back to its procedural avatar / agent-pubkey label.
  const agentDisplayName = useMemo(() => {
    const name = (profile?.displayName || '').trim();
    return name.length > 0 ? name : null;
  }, [profile]);
  const agentAvatarUrl = useMemo(() => {
    const url = (profile?.avatarUrl || '').trim();
    return url.length > 0 ? url : null;
  }, [profile]);

  const statusLabel = (chainRecord?.statusLabel || agent.status) as string;
  const isRevoked = statusLabel === 'revoked';
  const opIsClassic = operatorWallet?.startsWith('G') ?? false;
  const canSignRevoke = operatorWallet
    ? (opIsClassic ? !!stellarWallet : !!passkey)
    : false;

  const sameWalletAsAgent = passkey?.account.accountAddress === agent.address;
  const canRunResidencyChain = !!operatorWallet && sameWalletAsAgent && !!passkey && !isRevoked;

  const handleChangeResidency = useCallback(async () => {
    if (!passkey || !canRunResidencyChain) return;
    const target = targetCountry.toUpperCase();
    if (!/^[A-Z]{2}$/.test(target)) {
      setResidencyChainErr('Enter a 2-letter ISO country code.');
      return;
    }
    if (residency?.country?.toUpperCase() === target) {
      setResidencyChainErr('Agent is already resident in that country.');
      return;
    }
    setResidencyChainErr(null);
    setResidencyBusy(true);
    try {
      await runResidencyChangeChain({
        agentAddress:   agent.address,
        passkey,
        targetCountry:  target,
        currentCountry: residency?.country,
        onStep: (step, status) => {
          if (status === 'in_progress') setResidencyChainStep(step);
        },
      });
      await load();
      onResidencyChanged?.();
      setTargetCountry('');
    } catch (e: unknown) {
      setResidencyChainErr(humanizeUnknownError(e));
    } finally {
      setResidencyBusy(false);
      setResidencyChainStep(null);
    }
  }, [
    agent.address,
    canRunResidencyChain,
    load,
    onResidencyChanged,
    passkey,
    residency?.country,
    targetCountry,
  ]);

  const handleRevokeAgent = useCallback(async () => {
    if (!operatorWallet || isRevoked || !canSignRevoke) return;
    const msg =
      'Revoke this agent on-chain? It will no longer be active for MCP or policy. ' +
      'The registry keeps the record; you cannot register the same address again — use a new agent key if you need a fresh registration.';
    if (typeof window !== 'undefined' && !window.confirm(msg)) return;
    setRevokeErr(null);
    setRevokeBusy(true);
    try {
      const built = await buildRevokeTx({ operator: operatorWallet, agent: agent.address });
      let signedXdr: string;
      if (opIsClassic) {
        signedXdr = await stellarWallet!.signTransaction(built.xdr, resolveNetworkPassphrase());
      } else {
        signedXdr = await passkey!.signUnsignedXdr(built.xdr, passkey!.account, built.latestLedger);
      }
      await submitSignedAgentTx(signedXdr, { agentToRefresh: agent.address });
      onRevoked?.();
    } catch (e: unknown) {
      setRevokeErr(humanizeUnknownError(e));
    } finally {
      setRevokeBusy(false);
    }
  }, [
    agent.address,
    canSignRevoke,
    isRevoked,
    onRevoked,
    opIsClassic,
    operatorWallet,
    passkey,
    stellarWallet,
  ]);

  return (
    <aside style={detailPanelStyle}>
      <div style={detailHeaderStyle}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Agent</div>
          <div style={detailAddrStyle} title={agent.address}>{agent.address}</div>
          {onOpenWallet && (
            <button
              type="button"
              onClick={() => onOpenWallet(agent.address)}
              style={openProfileLinkStyle}
              title="Open this agent's profile (and its past broadcasts)"
            >
              Open profile ↗
            </button>
          )}
        </div>
        <button onClick={onClose} aria-label="Close" style={closeBtnStyle}>×</button>
      </div>

      {err && <div style={errorBoxStyle}>{err}</div>}

      {/* Status / Residency / Policy in a responsive 3-column grid so
          they share the same row on wide screens (the previous layout
          stacked them which forced a lot of scrolling in the cramped
          right column). */}
      <div style={detailMetaGridStyle}>
        <section style={metaCardStyle}>
          <h4 style={sectionTitleStyle}>Status</h4>
          <dl style={dlStyle}>
            <DT>Kind</DT><DD>{(chainRecord?.kindLabel || agent.kindLabel) ?? '—'}</DD>
            <DT>Status</DT>
            <DD>
              <span style={(chainRecord?.statusLabel || agent.status) === 'revoked' ? revokedPill : activePill}>
                {chainRecord?.statusLabel || agent.status}
              </span>
            </DD>
            <DT>Operator</DT>
            <DD style={monoStyle}>{shortAddr(chainRecord?.operator || agent.operator || '—')}</DD>
            <DT>Version</DT><DD>{chainRecord?.version ?? agent.version ?? '—'}</DD>
            <DT>Created (ledger)</DT><DD>{chainRecord?.createdAtLedger ?? agent.createdAtLedger ?? '—'}</DD>
            <DT>Updated (ledger)</DT><DD>{chainRecord?.updatedAtLedger ?? agent.updatedAtLedger ?? '—'}</DD>
          </dl>
        </section>

        <section style={metaCardStyle}>
          <h4 style={sectionTitleStyle}>Residency</h4>
          {!residency?.configured ? (
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              Citizenship contract not deployed on this env — residency guard is skipped.
            </div>
          ) : !residency.country ? (
            <>
              <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 10 }}>
                No active residency. Strict deploys reject position writes with{' '}
                <code style={capIdStyle}>no_active_residency</code>.
              </div>
              {canRunResidencyChain && (
                <ResidencyChangeForm
                  targetCountry={targetCountry}
                  onTargetCountryChange={setTargetCountry}
                  busy={residencyBusy}
                  chainStep={residencyChainStep}
                  error={residencyChainErr}
                  submitLabel="Apply for residency"
                  onSubmit={() => void handleChangeResidency()}
                />
              )}
              {operatorWallet && !canRunResidencyChain && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Browser apply requires your profile smart wallet to be this agent, or use MCP{' '}
                  <code style={capIdStyle}>apply_citizenship</code>.
                </div>
              )}
            </>
          ) : residency.active ? (
            <>
              <dl style={dlStyle}>
                <DT>Country</DT>
                <DD><span style={activePill}>{residency.country}</span></DD>
                <DT>Status</DT>
                <DD>
                  <span style={residency.active.statusName === 'active' ? activePill : revokedPill}>
                    {residency.active.statusName}
                  </span>
                </DD>
                <DT>Granted (ledger)</DT><DD>{residency.active.grantedAtLedger || '—'}</DD>
                <DT>Expires (ledger)</DT><DD>{residency.active.expiresAtLedger || '—'}</DD>
                <DT>Paid (stroops)</DT><DD>{residency.active.paidAmount}</DD>
                <DT>Policy version</DT><DD>{residency.active.policyVersion}</DD>
              </dl>
              {canRunResidencyChain && (
                <ResidencyChangeForm
                  targetCountry={targetCountry}
                  onTargetCountryChange={setTargetCountry}
                  busy={residencyBusy}
                  chainStep={residencyChainStep}
                  error={residencyChainErr}
                  submitLabel={`Change residency${targetCountry ? ` to ${targetCountry.toUpperCase()}` : ''}`}
                  onSubmit={() => void handleChangeResidency()}
                  currentCountry={residency.country}
                />
              )}
            </>
          ) : (
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              Active country reported as <code style={capIdStyle}>{residency.country}</code> but the underlying row could not be read.
            </div>
          )}
        </section>

        <section style={metaCardStyle}>
          <h4 style={sectionTitleStyle}>Policy snapshot</h4>
          {!policy?.configured ? (
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              Policy contract not deployed yet — caps and whitelist will live here once it ships.
            </div>
          ) : !policy.bound ? (
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              Agent has not been bound to a policy yet. Run <code style={capIdStyle}>connect_wallet</code> from the GeoTrust MCP server to attach policy limits.
            </div>
          ) : (
            <dl style={dlStyle}>
              <DT>Per-call cap</DT>            <DD>{fmtCap(policy.caps?.per_call)} XLM</DD>
              <DT>Daily cap</DT>                <DD>{fmtCap(policy.caps?.daily_xlm)} XLM</DD>
              <DT>Tx per day</DT>               <DD>{fmtCap(policy.caps?.tx_per_day)}</DD>
              <DT>Gas budget per call</DT>      <DD>{fmtCap(policy.caps?.gas_xlm)} XLM</DD>
              <DT>Expiry (ledger)</DT>          <DD>{policy.expiryLedger ?? '— never'}</DD>
              <DT>Spent in window</DT>          <DD>{policy.window ? `${policy.window.spentInWindow} (started @ ${policy.window.windowStartLedger})` : '—'}</DD>
            </dl>
          )}
        </section>
      </div>

      {operatorWallet && !isRevoked && (
        <AgentMovementPanel
          agentAddress={agent.address}
          operatorWallet={operatorWallet}
          residencyCountry={residency?.country || null}
          stellarWallet={stellarWallet}
          passkey={passkey}
        />
      )}

      {revokeErr && (
        <div style={{ ...registerErrStyle, marginTop: 12 }}>{revokeErr}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            style={previewBtnStyle}
            title="Preview the agent's broadcast canvas with test captions (no Azure cost)"
          >
            🤖 Preview
          </button>
          {operatorWallet && !isRevoked && (
            <button
              type="button"
              onClick={() => setLiveOpen(true)}
              style={liveBtnStyle}
              title="Open the paid live-broadcast flow for this agent. The operator wallet pays for the session; the agent identity is the broadcaster."
            >
              🔴 Go live as agent
            </button>
          )}
          {operatorWallet && !isRevoked && (
            <button
              type="button"
              onClick={() => setDisposeOpen(true)}
              style={disposeBtnStyle}
              title="Sweep all assets off the agent wallet, then revoke the registry record. Irreversible."
            >
              ♻︎ Dispose…
            </button>
          )}
          {operatorWallet && !isRevoked && (
            <button
              type="button"
              onClick={() => void handleRevokeAgent()}
              disabled={revokeBusy || !canSignRevoke}
              style={revokeBtnStyle}
              title="Sign revoke_agent in your wallet WITHOUT sweeping. Use Dispose instead unless the agent wallet is empty."
            >
              {revokeBusy ? 'Revoking…' : 'Revoke only'}
            </button>
          )}
        </div>
        <button onClick={() => void load()} disabled={busy} style={refreshBtnStyle}>
          {busy ? 'Refreshing…' : 'Refresh on-chain'}
        </button>
      </div>
      {operatorWallet && !canSignRevoke && !isRevoked && (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#b45309' }}>
          Connect your wallet (Freighter or passkey) to revoke this agent.
        </p>
      )}

      <AgentBroadcastPreviewModal
        agentPublicKey={agent.address}
        displayName={agentDisplayName}
        avatarUrl={agentAvatarUrl}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />

      <AgentLiveBroadcastModal
        agentPublicKey={agent.address}
        displayName={agentDisplayName}
        avatarUrl={agentAvatarUrl}
        operatorWallet={operatorWallet}
        country={viewerBroadcastCountry != null ? viewerBroadcastCountry : undefined}
        approxLocation={approxLocation}
        onExecuteLivePayment={onExecuteLivePayment}
        open={liveOpen}
        onClose={() => setLiveOpen(false)}
      />

      {operatorWallet && (
        <AgentDisposeModal
          open={disposeOpen}
          agentAddress={agent.address}
          operatorWallet={operatorWallet}
          passkey={passkey}
          onClose={() => setDisposeOpen(false)}
          onConfirmed={(params) => {
            setDisposeOpen(false);
            setDisposeInstructions(params);
          }}
        />
      )}

      {/*
        We can't sign the sweep on behalf of the agent from the
        browser — the agent's secret key lives in the MCP credential
        store. So once the operator confirms the dispose plan we
        surface the prepared params + the matching MCP tool call so
        they can paste it into their AI client (Claude Desktop /
        Cursor / etc.). The operator wallet still signs the final
        revoke_agent step here via the existing button above.
      */}
      {disposeInstructions && (
        <DisposeInstructionsCard
          agentAddress={agent.address}
          params={disposeInstructions}
          operatorWallet={operatorWallet || ''}
          onClose={() => setDisposeInstructions(null)}
          onRefresh={() => void load()}
        />
      )}
    </aside>
  );
};

// ── Dispose instructions card ──────────────────────────────────────────────
//
// Shown after the operator confirms the dispose plan in the modal.
// We can't run the sweep from the browser (agent secret lives in MCP),
// so this surfaces the prepared `agent_dispose` MCP tool call so the
// operator can paste it into their AI client. After they run it, the
// agent's wallet is empty and the operator clicks "Revoke only" to
// finalise the registry retirement.
const DisposeInstructionsCard: React.FC<{
  agentAddress:    string;
  operatorWallet:  string;
  params:          DisposeParams;
  onClose:         () => void;
  onRefresh:       () => void;
}> = ({ agentAddress, operatorWallet, params, onClose, onRefresh }) => {
  const [copied, setCopied] = useState(false);

  const toolPayload = useMemo(() => ({
    operator_secret:   '<paste your operator secret here>',
    target_account_id: params.targetAccountId,
    close_trustlines:  params.closeTrustlines,
    merge_account:     params.mergeAccount,
    dry_run:           false,
  }), [params]);

  const human = useMemo(() => {
    const lines = [
      `agent: ${agentAddress}`,
      `operator: ${operatorWallet || '—'}`,
      `target: ${params.targetAccountId}`,
      `close_trustlines: ${params.closeTrustlines}`,
      `merge_account: ${params.mergeAccount}`,
    ];
    return lines.join('\n');
  }, [agentAddress, operatorWallet, params]);

  const copyJson = useCallback(async () => {
    try {
      await navigator.clipboard?.writeText(JSON.stringify(toolPayload, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard not available — user can copy manually */
    }
  }, [toolPayload]);

  return (
    <div style={instructionsCardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={kickerStyle}>Step 1 · sweep via MCP</div>
          <h4 style={{ margin: '4px 0 4px', fontSize: 14, color: '#111827' }}>Run <code>agent_dispose</code> in your AI client</h4>
        </div>
        <button onClick={onClose} aria-label="Close" style={closeBtnStyle}>×</button>
      </div>
      <pre style={preStyle}>{human}</pre>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => void copyJson()} style={refreshBtnStyle}>
          {copied ? 'Copied!' : 'Copy MCP arguments JSON'}
        </button>
        <button onClick={onRefresh} style={refreshBtnStyle}>Refresh balances</button>
      </div>
      <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b7280' }}>
        After the MCP tool reports success, click <strong>Revoke only</strong> above to finalise the
        registry record. Refresh balances first to confirm the agent's wallet is empty.
      </p>
    </div>
  );
};

const instructionsCardStyle: React.CSSProperties = {
  marginTop: 12,
  padding: 12,
  borderRadius: 10,
  border: '1px solid #fde68a',
  background: '#fffbeb',
};

const kickerStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#92400e',
};

const preStyle: React.CSSProperties = {
  margin: '8px 0',
  padding: 10,
  background: '#fff',
  border: '1px solid #fde68a',
  borderRadius: 8,
  fontSize: 12,
  lineHeight: 1.4,
  fontFamily: 'monospace',
  color: '#374151',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};

const disposeBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 999,
  border: '1px solid #9a3412',
  background: '#9a3412',
  color: '#fff',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

// ── Register agent (on-chain) ────────────────────────────────────────────────

/**
 * Dual-auth co-signing for register_agent.
 *
 * The registry contract requires BOTH the operator AND the agent address
 * to authorize a registration (agent.require_auth) so nobody can squat an
 * address they don't control. The operator's authorization is handled by
 * the envelope signature (Freighter `G…`) or the operator passkey entry
 * (`C…`). This helper takes care of the AGENT's authorization when the
 * agent is a different address the viewer controls:
 *
 *   - agent is one of this device's smart accounts (C…): passkey-sign the
 *     agent's address-credential auth entry.
 *   - agent is an external classic key (G…): sign the entry locally with
 *     the pasted agent secret (S…) — the secret never leaves the browser.
 *
 * Backward-compatible by design: against the pre-upgrade registry (no
 * agent.require_auth) the simulation produces no agent auth entry, so this
 * is a no-op and the existing single-signature flow is unchanged.
 */
async function coSignAgentEntryIfPresent(opts: {
  unsignedXdr: string;
  operator: string;
  agent: string;
  latestLedger: number;
  agentSecret?: string;
}): Promise<string> {
  const { unsignedXdr, operator, agent, latestLedger, agentSecret } = opts;
  // operator == agent: a single auth entry, satisfied by the operator's
  // own signing path — nothing extra to co-sign here.
  if (agent === operator) return unsignedXdr;

  const sdk: any = await import('@stellar/stellar-sdk');
  const { TransactionBuilder, Keypair } = sdk;
  const passphrase = resolveNetworkPassphrase();
  const tx = TransactionBuilder.fromXDR(unsignedXdr, passphrase);
  const authEntries: any[] = tx.operations?.[0]?.auth ?? [];
  if (!authEntries.length) return unsignedXdr;

  const idx = findAuthEntryIndexForAddress(authEntries, agent);
  // No agent auth entry → pre-upgrade contract; leave the tx untouched.
  if (idx === -1) return unsignedXdr;

  if (agent.startsWith('C')) {
    const rec = passkeyKitService.getAccounts().find((a) => a.accountAddress === agent);
    if (!rec) {
      throw new Error(
        'This registry now requires the agent smart account to authorize its own registration, ' +
        'but that passkey account is not on this device.',
      );
    }
    // signTransaction mutates `tx` in place (signs the agent's entry) and
    // returns the updated XDR; the operator still signs the envelope after.
    return await passkeyKitService.signTransaction(tx, rec, latestLedger);
  }

  // External classic agent key (G…) — needs the agent secret to authorize.
  const secret = (agentSecret || '').trim();
  if (!secret) {
    throw new Error(
      'This registry requires the agent key to authorize its registration. ' +
      'Open Advanced and paste the agent secret (S…); it is signed locally and never sent.',
    );
  }
  let kp: any;
  try { kp = Keypair.fromSecret(secret); } catch { throw new Error('Invalid agent secret (expected an S… seed).'); }
  if (kp.publicKey() !== agent) {
    throw new Error('The agent secret does not match the agent address.');
  }
  const validUntil = latestLedger && latestLedger > 0 ? latestLedger + 500 : 0xffffffff;
  const signed = await authorizeEntryWithKeypair(
    authEntries[idx], kp, validUntil, passphrase, `Agent ${agent}`,
  );
  // SDK 17 credentials are immutable; replace the entry so the change serialises.
  authEntries[idx] = signed;
  return tx.toXDR();
}

const AgentRegistrationPanel: React.FC<{
  subjectWallet: string;
  schema: CapabilityEntry[];
  stellarWallet: SigningWallet | null;
  passkey: PasskeySigner | null;
  onSuccess: (agentAddress: string) => void;
  /** Defaults to "Register an agent on-chain". */
  panelTitle?: string;
}> = ({ subjectWallet, schema, stellarWallet, passkey, onSuccess, panelTitle }) => {
  const [agentPk, setAgentPk] = useState('');
  /** Register `subjectWallet` as the agent — valid for passkey (`C…`) or classic (`G…`) operators. */
  const [sameWalletAsAgent, setSameWalletAsAgent] = useState(false);
  /**
   * Advanced escape hatch: register an EXTERNAL agent key (e.g. a
   * `genAgent` G-address from the MCP onboarding flow) that isn't one
   * of this device's own smart accounts. Opens the free-text input.
   */
  const [advanced, setAdvanced] = useState(false);
  /**
   * Agent secret (S…) for the Advanced/external path. Only used once the
   * registry enforces agent.require_auth — signed locally, never sent.
   */
  const [agentSecret, setAgentSecret] = useState('');
  const [kind, setKind] = useState(0);

  /**
   * The viewer's own smart accounts (C…) from this device's passkey-kit
   * store — the safe set to register as agents. We prefer accounts
   * linked to this profile wallet; if none are explicitly linked we
   * fall back to every locally-stored account (all of them are
   * controlled from this browser, so they're still the user's).
   */
  const myAccounts = useMemo(() => {
    let accts: { accountAddress: string }[] = [];
    try { accts = passkeyKitService.getAccounts(); } catch { accts = []; }
    const linked = accts.filter(
      (a) => (a as { walletAddress?: string }).walletAddress === subjectWallet,
    );
    const pool = linked.length > 0 ? linked : accts;
    const seen = new Set<string>();
    return pool
      .map((a) => a.accountAddress)
      .filter((addr) => STELLAR_ADDRESS.test(addr) && addr !== subjectWallet && !seen.has(addr) && seen.add(addr));
  }, [subjectWallet]);
  const [selected, setSelected] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(schema.map((c) => [c.id, true])),
  );
  const [busy, setBusy] = useState(false);
  const [regErr, setRegErr] = useState<string | null>(null);

  useEffect(() => {
    setSelected((prev) => {
      const next = { ...prev };
      for (const c of schema) {
        if (next[c.id] === undefined) next[c.id] = true;
      }
      return next;
    });
  }, [schema]);

  const opIsClassic = subjectWallet.startsWith('G');
  const canSign = opIsClassic ? !!stellarWallet : !!passkey;
  const effectiveAgent = sameWalletAsAgent ? subjectWallet.trim() : agentPk.trim();
  const agentOk = STELLAR_ADDRESS.test(effectiveAgent);
  const capsList = useMemo(
    () => schema.filter((c) => selected[c.id]).map((c) => c.id),
    [schema, selected],
  );

  async function handleRegister() {
    setRegErr(null);
    if (!agentOk) {
      setRegErr(
        sameWalletAsAgent
          ? 'Profile wallet address is invalid.'
          : 'Enter a valid agent address: classic account G… or contract C… (56 characters).',
      );
      return;
    }
    if (!canSign) {
      setRegErr(
        opIsClassic
          ? 'Connect a Stellar wallet (Freighter) so you can sign register_agent.'
          : 'Passkey signing is required for smart-wallet operators — ensure your passkey account is loaded.',
      );
      return;
    }
    if (capsList.length === 0) {
      setRegErr('Select at least one capability.');
      return;
    }
    const agent = effectiveAgent;
    setBusy(true);
    try {
      const built = await buildRegisterTx({
        operator: subjectWallet,
        agent,
        kind,
        capabilities: capsList,
        external: advanced && !sameWalletAsAgent,
      });
      // Co-sign the agent's authorization entry when the upgraded registry
      // requires it (no-op against the current single-auth contract).
      const preparedXdr = await coSignAgentEntryIfPresent({
        unsignedXdr: built.xdr,
        operator: subjectWallet,
        agent,
        latestLedger: built.latestLedger,
        agentSecret: advanced && !sameWalletAsAgent ? agentSecret : '',
      });
      let signedXdr: string;
      if (opIsClassic) {
        signedXdr = await stellarWallet!.signTransaction(preparedXdr, resolveNetworkPassphrase());
      } else {
        signedXdr = await passkey!.signUnsignedXdr(preparedXdr, passkey!.account, built.latestLedger);
      }
      await submitSignedAgentTx(signedXdr, { agentToRefresh: agent });
      setAgentPk('');
      setAgentSecret('');
      setSameWalletAsAgent(false);
      onSuccess(agent);
    } catch (e: unknown) {
      setRegErr(humanizeUnknownError(e));
    } finally {
      setBusy(false);
    }
  }

  const toggle = (id: string) => {
    setSelected((s) => ({ ...s, [id]: !s[id] }));
  };

  return (
    <section style={registerSectionStyle}>
      <h3 style={{ margin: '0 0 8px', fontSize: 15, color: '#111827' }}>
        {panelTitle || 'Register an agent on-chain'}
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280', lineHeight: 1.45 }}>
        Most operators register a <strong>separate</strong> agent key pair (from <code style={inlineCodeStyle}>genAgent</code> / MCP).
        Passkey-only flows can instead register <strong>this profile wallet</strong> as the agent so one identity signs everything (policy still caps what contracts can run).
      </p>
      <label style={{ ...capPickRowStyle, marginBottom: 10 }}>
        <input
          type="checkbox"
          checked={sameWalletAsAgent}
          onChange={(e) => {
            setSameWalletAsAgent(e.target.checked);
            setRegErr(null);
          }}
        />
        <span style={{ fontSize: 13, color: '#374151' }}>
          Use my profile wallet as the agent ({subjectWallet.startsWith('C') ? 'passkey smart wallet' : 'this account'})
        </span>
      </label>
      {!sameWalletAsAgent && !advanced && (
        <>
          <label style={labelStyle}>
            Agent (one of your smart accounts)
            {myAccounts.length > 0 ? (
              <select
                value={agentPk}
                onChange={(e) => { setAgentPk(e.target.value.trim()); setRegErr(null); }}
                style={inputStyle}
              >
                <option value="">Select a smart account…</option>
                {myAccounts.map((addr) => (
                  <option key={addr} value={addr}>
                    {addr.slice(0, 8)}…{addr.slice(-6)}
                  </option>
                ))}
              </select>
            ) : (
              <div
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: '#f3f4f6',
                  border: '1px dashed #d1d5db',
                  fontSize: 12,
                  color: '#6b7280',
                }}
              >
                No smart accounts found on this device. Tick "Use my profile wallet"
                above, or use Advanced to register an external agent key.
              </div>
            )}
          </label>
          <button
            type="button"
            onClick={() => { setAdvanced(true); setAgentPk(''); setAgentSecret(''); setRegErr(null); }}
            style={advancedToggleStyle}
          >
            Advanced: external agent key (G…)
          </button>
        </>
      )}
      {!sameWalletAsAgent && advanced && (
        <>
          <label style={labelStyle}>
            External agent address (G… separate key, or C… contract)
            <input
              type="text"
              value={agentPk}
              onChange={(e) => setAgentPk(e.target.value.trim())}
              placeholder="G… or C…"
              style={inputStyle}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
            Use this for a <code style={inlineCodeStyle}>genAgent</code> / MCP key that
            isn't one of this device's smart accounts. You must control this address.
          </p>
          {agentPk.startsWith('G') && (
            <label style={labelStyle}>
              Agent secret (S…) — only needed if the registry requires the agent to authorize
              <input
                type="password"
                value={agentSecret}
                onChange={(e) => setAgentSecret(e.target.value.trim())}
                placeholder="S… (signed locally, never sent)"
                style={inputStyle}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
          )}
          <button
            type="button"
            onClick={() => { setAdvanced(false); setAgentPk(''); setAgentSecret(''); setRegErr(null); }}
            style={advancedToggleStyle}
          >
            ← Back to my accounts
          </button>
        </>
      )}
      {sameWalletAsAgent && (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            background: '#eef2ff',
            border: '1px solid #c7d2fe',
            fontSize: 13,
            color: '#3730a3',
          }}
        >
          Agent will be <code style={inlineCodeStyle}>{subjectWallet}</code>
        </div>
      )}
      <label style={labelStyle}>
        Kind
        <select value={kind} onChange={(e) => setKind(Number(e.target.value))} style={inputStyle}>
          <option value={0}>0 — generic</option>
          <option value={1}>1 — trader</option>
          <option value={2}>2 — guide</option>
          <option value={3}>3 — pilot</option>
        </select>
      </label>
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Capabilities</div>
        <div style={capPickGridStyle}>
          {schema.map((c) => (
            <label key={c.id} style={capPickRowStyle}>
              <input
                type="checkbox"
                checked={!!selected[c.id]}
                onChange={() => toggle(c.id)}
              />
              <code style={inlineCodeStyle}>{c.id}</code>
              <span style={{ color: '#6b7280' }}>{c.label}</span>
            </label>
          ))}
        </div>
      </div>
      {regErr && <div style={registerErrStyle}>{regErr}</div>}
      <button
        type="button"
        onClick={() => void handleRegister()}
        disabled={busy || !agentOk || !canSign || capsList.length === 0}
        style={registerBtnStyle}
      >
        {busy ? 'Signing / submitting…' : 'Sign register_agent in wallet'}
      </button>
      {!canSign && (
        <p style={{ margin: '10px 0 0', fontSize: 12, color: '#b45309' }}>
          {opIsClassic ? 'No signing wallet connected.' : 'Passkey signer not available for this profile.'}
        </p>
      )}
    </section>
  );
};

// ── Empty state ────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ isOwner: boolean; schema: CapabilityEntry[] | null }> = ({
  isOwner,
  schema,
}) => (
  <div style={emptyBoxStyle}>
    <h3 style={{ margin: 0 }}>
      {isOwner ? 'No agents yet' : 'No agents registered'}
    </h3>
    <p style={{ marginTop: 8, color: '#6b7280', fontSize: 14 }}>
      {isOwner ? (
        <>
          No agents yet — register below with your connected wallet, or use the GeoTrust MCP server to generate a key and submit the same transaction.
          Agents are tied to your operator wallet on-chain; spending is bounded by policy.
        </>
      ) : (
        <>This operator hasn't registered any AI agents.</>
      )}
    </p>
  </div>
);

// ── Capability schema preview ──────────────────────────────────────────────

const CAPABILITY_CATEGORY_ORDER = ['identity', 'citizenship', 'movement', 'broadcast', 'vehicle'];

function capabilityCategoryTitle(category: string): string {
  const titles: Record<string, string> = {
    identity: 'Wallet & profile',
    citizenship: 'Citizenship & residency',
    movement: 'Map & movement',
    broadcast: 'Live broadcast',
    vehicle: 'Drones & vehicles',
  };
  const k = category || 'other';
  return titles[k] || (k.length ? k.charAt(0).toUpperCase() + k.slice(1) : 'Other');
}

const CapabilitySchemaPreview: React.FC<{ capabilities: CapabilityEntry[] }> = ({
  capabilities,
}) => {
  const grouped = useMemo(() => {
    const rank = new Map(CAPABILITY_CATEGORY_ORDER.map((c, i) => [c, i]));
    const m = new Map<string, CapabilityEntry[]>();
    capabilities.forEach((c) => {
      const key = (c.category && String(c.category)) || 'other';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(c);
    });
    return Array.from(m.entries()).sort((a, b) => {
      const ia = rank.has(a[0]) ? rank.get(a[0])! : 100;
      const ib = rank.has(b[0]) ? rank.get(b[0])! : 100;
      if (ia !== ib) return ia - ib;
      return a[0].localeCompare(b[0]);
    });
  }, [capabilities]);

  return (
    <section style={{ ...sectionStyle, marginTop: 24 }}>
      <details style={collapsibleDetailsStyle}>
        <summary style={collapsibleSummaryStyle}>
          MCP tool reference (advanced)
        </summary>
        <p style={collapsibleIntroStyle}>
          Short IDs below match what the operator MCP grants with{' '}
          <code style={inlineCodeStyle}>set_capabilities</code>. You do not need to memorise them unless you are configuring agents via MCP.
        </p>
        {grouped.map(([category, caps]) => (
          <div key={category} style={{ marginTop: 14 }}>
            <div style={schemaCategoryHeaderStyle}>{capabilityCategoryTitle(category)}</div>
            <ul style={capListStyle}>
              {caps.map((c) => (
                <li key={c.id} style={capItemStyle}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <code style={capIdStyle}>{c.id}</code>
                    <span style={capLabelStyle}>{c.label}</span>
                    {c.writes_chain && <span style={chainTagStyle}>Stellar write</span>}
                    {c.cost_xlm > 0 && <span style={costTagStyle}>{c.cost_xlm} XLM</span>}
                  </div>
                  <div style={capDescStyle}>{c.description}</div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </details>
    </section>
  );
};

// ── Helpers / styles ───────────────────────────────────────────────────────

function shortAddr(a: string): string {
  if (!a) return '';
  return a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-6)}` : a;
}

function fmtCap(s: string | null | undefined): string {
  if (s === null || s === undefined) return '—';
  return s;
}

const DT: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <dt style={{ color: '#6b7280', fontSize: 12, fontWeight: 600 }}>{children}</dt>
);
const DD: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => <dd style={{ margin: 0, fontSize: 13, color: '#111827', ...style }}>{children}</dd>;

const tabRoot: React.CSSProperties = {
  padding: '8px 4px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
};

const toolbarActionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
};

const toolbarBtnStylePrimary: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid #4338ca',
  background: 'linear-gradient(135deg, #6366f1, #4338ca)',
  color: '#fff',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  letterSpacing: 0.2,
};

const toolbarBtnStyleActive: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid #cbd5f5',
  background: '#eef2ff',
  color: '#312e81',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const countPillStyle: React.CSSProperties = {
  padding: '2px 10px',
  borderRadius: 999,
  background: '#eef2ff',
  color: '#3730a3',
  border: '1px solid #c7d2fe',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
};

const dashboardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const agentRailStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 8,
};

const railCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  background: '#fff',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
  transition: 'border-color 120ms ease, transform 120ms ease',
};

const railCardActiveStyle: React.CSSProperties = {
  borderColor: '#4f46e5',
  background: 'linear-gradient(135deg, #eef2ff, #f5f3ff)',
  boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.18)',
};

const railCardRevokedStyle: React.CSSProperties = { opacity: 0.7 };

const railCardTitleStyle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: 13,
  color: '#111827',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const railCardMetaStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#6b7280',
  marginTop: 2,
};

const railMovementMetaStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#16a34a',
  marginTop: 4,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const railTeleportBtnStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRadius: 6,
  border: '1px solid #c7d2fe',
  background: '#eef2ff',
  color: '#3730a3',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const stopAllBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid #b91c1c',
  background: '#fff',
  color: '#b91c1c',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const railCapChipsStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginTop: 6,
};

const railCapChipStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  padding: '2px 6px',
  borderRadius: 999,
  background: '#eef2ff',
  color: '#3730a3',
  border: '1px solid #c7d2fe',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const railCapChipMoreStyle: React.CSSProperties = {
  ...railCapChipStyle,
  background: '#f3f4f6',
  color: '#6b7280',
  borderColor: '#e5e7eb',
};

const railCardBadgeStyle: React.CSSProperties = {
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  border: '1px solid transparent',
  whiteSpace: 'nowrap',
};

const stalePillStyle: React.CSSProperties = {
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
  background: '#fef3c7',
  color: '#92400e',
  border: '1px solid #fde68a',
};

const staleBannerStyle: React.CSSProperties = {
  background: '#fffbeb',
  border: '1px solid #fcd34d',
  borderRadius: 12,
  padding: '12px 14px',
  color: '#92400e',
  marginBottom: 4,
};

const emptyDetailHintStyle: React.CSSProperties = {
  padding: 16,
  borderRadius: 10,
  background: '#fff',
  border: '1px dashed #cbd5e1',
  color: '#475569',
  fontSize: 13,
  lineHeight: 1.5,
};
const registerSectionStyle: React.CSSProperties = {
  marginTop: 16,
  padding: '14px 16px',
  borderRadius: 12,
  border: '1px solid #d1d5db',
  background: '#fafafa',
};
const syncPanelStyle: React.CSSProperties = {
  marginBottom: 12,
  padding: '12px 14px',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  background: '#f9fafb',
};
const advancedToggleStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  marginBottom: 12,
  padding: 0,
  border: 'none',
  background: 'none',
  color: '#4f46e5',
  fontSize: 12,
  cursor: 'pointer',
  textDecoration: 'underline',
};
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 10,
};
const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 13,
  fontFamily: 'ui-monospace, monospace',
};
const capPickGridStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  maxHeight: 220,
  overflowY: 'auto',
};
const capPickRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12,
};
const registerErrStyle: React.CSSProperties = {
  marginTop: 10,
  padding: '8px 10px',
  borderRadius: 8,
  background: '#fef2f2',
  color: '#b91c1c',
  fontSize: 13,
};
const registerBtnStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '10px 16px',
  borderRadius: 8,
  border: 'none',
  background: '#0369a1',
  color: '#fff',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

const titleStyle: React.CSSProperties = { margin: 0, fontSize: 18 };
const subTitleStyle: React.CSSProperties = { margin: '4px 0 0', color: '#6b7280', fontSize: 13, maxWidth: 720 };

const previewBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 999,
  border: '1px solid #4f46e5',
  background: '#6366f1',
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const liveBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 999,
  border: '1px solid #b91c1c',
  background: '#dc2626',
  color: '#fff',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const revokeBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 999,
  border: '1px solid #9a3412',
  background: '#fff',
  color: '#9a3412',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const refreshBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 999,
  border: '1px solid #e5e7eb',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  color: '#111827',
};

const residencyInputStyle: React.CSSProperties = {
  width: 56,
  padding: '6px 8px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontFamily: 'monospace',
  fontSize: 13,
};

const residencyBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #c2410c',
  background: '#fff7ed',
  color: '#c2410c',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const errorBoxStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: '#fef2f2',
  color: '#991b1b',
  borderRadius: 8,
  fontSize: 13,
  marginBottom: 12,
};

const detailPanelStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 16,
  background: '#fafafa',
  minWidth: 0,
};
const detailHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 12,
};
const detailAddrStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  wordBreak: 'break-all',
};
const openProfileLinkStyle: React.CSSProperties = {
  marginTop: 4,
  padding: 0,
  border: 'none',
  background: 'none',
  color: '#6366f1',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
};
const closeBtnStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  fontSize: 22,
  lineHeight: 1,
  cursor: 'pointer',
  color: '#6b7280',
};

const sectionStyle: React.CSSProperties = { marginTop: 12 };

const collapsibleDetailsStyle: React.CSSProperties = {
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  padding: '10px 12px',
  background: '#fff',
};

const collapsibleSummaryStyle: React.CSSProperties = {
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 14,
  color: '#111827',
  outline: 'none',
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  flexWrap: 'wrap',
};

const collapsibleSummaryHintStyle: React.CSSProperties = {
  fontWeight: 500,
  fontSize: 12,
  color: '#6b7280',
  fontFamily: 'monospace',
};

const collapsibleIntroStyle: React.CSSProperties = {
  color: '#6b7280',
  fontSize: 13,
  marginTop: 10,
  marginBottom: 0,
  lineHeight: 1.45,
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '12px 0 8px',
  fontSize: 14,
  fontWeight: 700,
  color: '#111827',
};

const dlStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(120px, max-content) 1fr',
  rowGap: 4,
  columnGap: 12,
  margin: 0,
};
const monoStyle: React.CSSProperties = { fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' };

const capListStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

// Two-column responsive grid so the granted-capabilities list uses
// the dashboard's full width without stretching every row across the
// whole panel. Cards self-resize at 320px breakpoints so on narrow
// viewports we degrade to a single column gracefully.
const capGridStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: 8,
};

// Status / Residency / Policy sit on the same row up to 3 columns wide
// in the new dashboard. The minmax floor matches `capGridStyle` so the
// two grids align visually even when only one populated column ends up
// wrapping.
const detailMetaGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 12,
  marginTop: 12,
};

const metaCardStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderRadius: 10,
  background: '#fff',
  border: '1px solid #e5e7eb',
};
const capItemStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: '#fff',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
};
const capIdStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  color: '#4338ca',
  background: '#eef2ff',
  padding: '1px 6px',
  borderRadius: 4,
};
const capLabelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#111827' };
const capDescStyle: React.CSSProperties = { color: '#6b7280', fontSize: 12, marginTop: 4 };
const capsHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  marginBottom: 8,
  flexWrap: 'wrap',
};
const capsEditBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 8,
  border: '1px solid #c7d2fe',
  background: '#eef2ff',
  color: '#3730a3',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const capsSaveBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 8,
  border: '1px solid #16a34a',
  background: '#16a34a',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};
const capsCancelBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#374151',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const capBadgeAddStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  padding: '2px 6px',
  borderRadius: 999,
  background: 'rgba(34,197,94,0.12)',
  color: '#15803d',
  border: '1px solid rgba(34,197,94,0.4)',
};
const capBadgeRemoveStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  padding: '2px 6px',
  borderRadius: 999,
  background: 'rgba(244,114,182,0.12)',
  color: '#9d174d',
  border: '1px solid rgba(244,114,182,0.4)',
};
const inlineCodeStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  background: '#f3f4f6',
  padding: '1px 4px',
  borderRadius: 4,
};

const chainTagStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: '#6b21a8',
  background: '#f5f3ff',
  border: '1px solid #ddd6fe',
  padding: '0 6px',
  borderRadius: 999,
};
const costTagStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: '#92400e',
  background: '#fef3c7',
  border: '1px solid #fde68a',
  padding: '0 6px',
  borderRadius: 999,
};
const kindPill: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: '#0369a1',
  background: '#e0f2fe',
  padding: '0 6px',
  borderRadius: 999,
};
const activePill: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: '#065f46',
  background: '#d1fae5',
  padding: '1px 8px',
  borderRadius: 999,
};
const revokedPill: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: '#991b1b',
  background: '#fee2e2',
  padding: '1px 8px',
  borderRadius: 999,
};

const emptyBoxStyle: React.CSSProperties = {
  border: '1px dashed #d1d5db',
  borderRadius: 12,
  padding: 24,
  textAlign: 'left',
  background: '#fafafa',
};

const schemaCategoryHeaderStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: '#6b7280',
  marginBottom: 6,
};

export default AgentsTab;
